import compact from "lodash/compact";
import filter from "lodash/filter";
import isEqual from "lodash/isEqual";
import { Button } from "PFComponents/button";
import { Inline, Stack } from "PFComponents/containers";
import { Divider } from "PFComponents/divider";
import { Radios } from "PFComponents/radios";
import SidePanel from "PFComponents/side_panel/side_panel";
import { Typography } from "PFComponents/typography";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";

import { ConflictResolution } from "../hooks/use_conflict_resolver";
import {
  findConflict,
  getConflictIdentifier,
  parseSelectedValueString,
  stringifySelectedValue
} from "../hooks/use_conflict_resolver.utils";
import { Conflict } from "../skills_frameworks_side_panel.types";

type ConflictsReviewSidePanelProps = {
  conflicts: Conflict[];
  onSave: (resolution: ConflictResolution[]) => void;
  onClose: VoidFunction;
};

export const ConflictsReviewSidePanel = ({ conflicts, onSave, onClose }: ConflictsReviewSidePanelProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "form.sections.skillsFrameworks.sidePanel" });
  const { t: tTranslation } = useTranslation();
  const [resolution, setResolution] = useState<ConflictResolution[]>([]);

  const handleConflictResolve = (newConflictResolution: ConflictResolution) => {
    const { customFieldId, customValueId, newSelected } = newConflictResolution;
    const conflict = findConflict(conflicts, customFieldId, customValueId);
    const isChanged = !isEqual(conflict?.selected, newSelected);

    setResolution(
      compact([
        ...filter(
          resolution,
          (conflictResolution) =>
            getConflictIdentifier(conflictResolution) !== getConflictIdentifier(newConflictResolution)
        ),
        isChanged && newConflictResolution
      ])
    );
  };

  const footerRenderer = ({ onSidePanelClose }) => (
    <Inline justifyContent="space-between">
      <Button kind="secondary" onClick={onSidePanelClose}>
        {tTranslation("cancel")}
      </Button>
      <Button
        onClick={() => {
          onSave(resolution);
          onSidePanelClose();
        }}
      >
        {tTranslation("save")}
      </Button>
    </Inline>
  );

  return (
    <SidePanel
      fullHeight
      show
      title={t("reviewConflicts")}
      footerRenderer={footerRenderer}
      onClose={onClose}
      noPortal
    >
      <Stack gap="spacingLg">
        {conflicts.map((conflict, index) => {
          const { conflictTitle, options, selected, customFieldId, customValueId } = conflict;
          const conflictIdentifier = getConflictIdentifier(conflict);
          const value =
            resolution.find(
              (conflictResolution) =>
                getConflictIdentifier(conflictResolution) === getConflictIdentifier(conflict)
            )?.newSelected ?? selected;

          return (
            <Fragment key={conflictIdentifier}>
              <Stack gap="spacingSm">
                <Typography variant="labelRegular">{conflictTitle}</Typography>
                <Radios
                  name={conflictIdentifier}
                  items={options.map(({ value, title, subtitle }) => ({
                    id: value,
                    label: title,
                    sublabel: subtitle
                  }))}
                  value={stringifySelectedValue(value)}
                  onChange={(newSelectedString) =>
                    handleConflictResolve({
                      customFieldId,
                      customValueId,
                      newSelected: parseSelectedValueString(newSelectedString)
                    })
                  }
                />
              </Stack>
              {index < conflicts.length - 1 && <Divider />}
            </Fragment>
          );
        })}
      </Stack>
    </SidePanel>
  );
};
